import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useConfigStore } from '../../store/configStore';
import axiosInstance from '../../lib/axios';
import { extractTextFromContent } from '../../lib/textExtract';
import { notifyCreditsCharged, openCreditsPanel } from '../../lib/credits';
import { tokensToWords } from '../../lib/tokensToWords';
import { toast } from 'sonner';
import useChatSessions from './useChatSessions';
import ChatHeader from './ChatHeader';
import SessionList from './SessionList';
import { ContextControls, HistoryLimitControl, DescribeImagesBar, SheetPicker } from './ContextSelector';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import DeleteSessionDialog from './DeleteSessionDialog';
import NewSessionPromptDialog from './NewSessionPromptDialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Button } from '../ui/button';
import { SquarePen, ChevronDown, ChevronUp, ChevronRight, FileText, Layers, Ban, Globe, SquareStack, RotateCcw, ClipboardList, Pencil, Plus, Cpu, Gauge, Paperclip, BookMarked } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import { useCommandsStore } from '../../store/commandsStore';

// Walk a sheet's TipTap JSON and collect its images (for the "send images to
// describe" opt-in). Returns {sheet_id, src, name, hasDescription} per image.
function collectImages(content, sheetId, acc) {
  const walk = (n) => {
    if (Array.isArray(n)) { n.forEach(walk); return; }
    if (n && typeof n === 'object') {
      if (n.type === 'resizableImage' && n.attrs && n.attrs.src) {
        const a = n.attrs;
        acc.push({
          sheet_id: sheetId,
          src: a.src,
          name: (a.alt || a.title || String(a.src).split('/').pop() || 'imagen').trim(),
          hasDescription: !!a.imageDescription,
        });
      }
      if (Array.isArray(n.content)) n.content.forEach(walk);
    }
  };
  if (content && typeof content === 'object') walk(content);
}

// T-151: prompt de auto-continuación que el cliente envía entre rondas. El
// backend lo persiste como mensaje de USER; al recargar la sesión aparece como
// burbuja azul y confunde al usuario, así que lo filtramos al renderizar.
// Una sola constante compartida entre el envío y el filtro = única fuente de
// verdad. El filtro es por MATCH EXACTO del texto: si algún día se cambia el
// texto del prompt, basta con cambiarlo aquí (acceptable para v1).
const AUTO_CONTINUE_PROMPT = 'Continúa exactamente donde te quedaste con la tarea pendiente. No repitas los cambios ya aplicados; cuando todo esté listo, dilo y termina.';

/**
 * ChatSidebar - Main component for the AI chat interface
 * Provides context-aware chat functionality with session management
 */
const ChatSidebar = ({
  documentId,
  sheets,
  activeSheetId,
  onClose,
  onInsertText,
  onReplaceText,
  documentPrompt,
  onContextChange,
  onToggleSize,
  isExpanded,
  onSessionChange,
  onApplyProposal,
  runLocked,
  onDocumentModified,
  onOpenCommandManager
}) => {
  const { apiKey, model, chatHistoryLimit, setChatHistoryLimit, chatGlobal, setChatGlobal } = useConfigStore();
  const { commands } = useCommandsStore();

  // T-147: el scope de las sesiones depende del toggle "Chat global vs por
  // hoja". En modo GLOBAL (chatGlobal=true) pasamos sheetId=null: el backend
  // devuelve TODAS las sesiones del documento y, clave, el useEffect de
  // useChatSessions (que depende de [documentId, sheetId]) NO se re-dispara
  // al cambiar de hoja -> la conversación se mantiene. En modo por-hoja,
  // comportamiento actual (activeSheetId).
  const sessionManager = useChatSessions(documentId, chatGlobal ? null : activeSheetId);

  // T-174: plantilla de razonamiento por sesión de chat. El usuario adjunta un
  // comando marcado como `chatTemplate` a la sesión; su texto viaja en el body
  // de cada mensaje como `reasoning_template`. La elección persiste en
  // localStorage por sesión (clave `chat_reasoning_template:{sessionId}`).
  const [reasoningTemplateId, setReasoningTemplateId] = useState(null);

  const chatTemplateCommands = useMemo(
    // T-237: todo comando de texto es plantilla por defecto; chatTemplate legado sigue contando.
    () => (commands || []).filter((c) => c.category === 'Texto' || !c.category || c.chatTemplate === true),
    [commands]
  );

  // Al cambiar de sesión, releer la plantilla guardada para esa sesión.
  useEffect(() => {
    const sid = sessionManager.currentSessionId;
    if (!sid) {
      setReasoningTemplateId(null);
      return;
    }
    try {
      const stored = localStorage.getItem(`chat_reasoning_template:${sid}`);
      setReasoningTemplateId(stored || null);
    } catch {
      setReasoningTemplateId(null);
    }
  }, [sessionManager.currentSessionId]);

  // Si el id guardado ya no existe o el comando perdió el flag, tratar como
  // "ninguna" (no romper).
  const effectiveReasoningTemplateId = useMemo(() => {
    if (!reasoningTemplateId) return null;
    const cmd = chatTemplateCommands.find((c) => c.id === reasoningTemplateId);
    return cmd ? reasoningTemplateId : null;
  }, [reasoningTemplateId, chatTemplateCommands]);

  const reasoningTemplateText = useMemo(() => {
    if (!effectiveReasoningTemplateId) return null;
    const cmd = chatTemplateCommands.find((c) => c.id === effectiveReasoningTemplateId);
    if (!cmd || !Array.isArray(cmd.steps)) return null;
    const text = cmd.steps.map((s) => s.instruction).filter(Boolean).join('\n\n');
    return text || null;
  }, [effectiveReasoningTemplateId, chatTemplateCommands]);

  // Ref mirror para evitar stale closure (M-17): sendMessage lee el ref, no el
  // state recién seteado en el mismo handler.
  const reasoningTemplateTextRef = useRef(reasoningTemplateText);
  reasoningTemplateTextRef.current = reasoningTemplateText;

  const handleReasoningTemplateChange = (commandId) => {
    const sid = sessionManager.currentSessionId;
    const newId = commandId || null;
    setReasoningTemplateId(newId);
    if (sid) {
      try {
        if (newId) {
          localStorage.setItem(`chat_reasoning_template:${sid}`, newId);
        } else {
          localStorage.removeItem(`chat_reasoning_template:${sid}`);
        }
      } catch { /* best-effort persistence */ }
    }
  };

  // T-208: modelo elegido por el usuario para el chat (de la lista de
  // chat-models). Persistido en localStorage (global del usuario, NO por
  // sesión). Si es null → "Default del sistema" (no se manda override de
  // model; el backend usa su default / BYOK como antes).
  const [chatModels, setChatModels] = useState([]);
  const [modelChoice, setModelChoice] = useState(null);

  // Cargar la lista de modelos disponibles para el chat (GET /ai/chat-models).
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/ai/chat-models')
      .then((r) => {
        if (cancelled) return;
        // La respuesta puede ser un array directo o venir envuelta en {models}.
        const list = Array.isArray(r.data) ? r.data : (r.data?.models || []);
        setChatModels(list);
      })
      .catch(() => { if (!cancelled) setChatModels([]); });
    return () => { cancelled = true; };
  }, []);

  // Cargar la elección persistida del usuario (global, no por sesión).
  useEffect(() => {
    try {
      const stored = localStorage.getItem('chat_model_choice');
      setModelChoice(stored || null);
    } catch {
      setModelChoice(null);
    }
  }, []);

  const handleModelChoice = (modelId) => {
    const newChoice = modelId || null;
    setModelChoice(newChoice);
    try {
      if (newChoice) {
        localStorage.setItem('chat_model_choice', newChoice);
      } else {
        localStorage.removeItem('chat_model_choice');
      }
    } catch { /* best-effort persistence */ }
  };

  // Ref mirror para evitar stale closure en sendMessage (M-17).
  const modelChoiceRef = useRef(null);
  modelChoiceRef.current = modelChoice;

  // T-226: esfuerzo de razonamiento para el chat (cuánto "piensa" el modelo
  // antes de responder). Valores: minimal | low | medium | high | xhigh.
  // DEFAULT 'medium'. Persistido en localStorage (global, no por sesión), mismo
  // patrón que chat_model_choice (T-208). El backend lo implementa en paralelo.
  const EFFORT_OPTIONS = [
    { value: 'minimal', label: 'Mínimo', desc: 'Respuestas rápidas y directas' },
    { value: 'low',     label: 'Bajo',   desc: 'Piensa poco; para preguntas sencillas' },
    { value: 'medium',  label: 'Medio (recomendado)',  desc: 'Equilibrio entre rapidez y calidad; el default' },
    { value: 'high',    label: 'Alto', desc: 'Razona con cuidado' },
    { value: 'xhigh',   label: 'Máximo', desc: 'Razona a fondo; más lento y más créditos' },
  ];
  const [effortChoice, setEffortChoice] = useState('medium');

  // Cargar la elección persistida del usuario (global, no por sesión). Si el
  // valor guardado no es uno de los 5 válidos → cae a 'medium' (default).
  useEffect(() => {
    try {
      const stored = localStorage.getItem('chat_effort');
      const valid = stored && ['minimal', 'low', 'medium', 'high', 'xhigh'].includes(stored);
      setEffortChoice(valid ? stored : 'medium');
    } catch {
      setEffortChoice('medium');
    }
  }, []);

  const handleEffortChoice = (v) => {
    setEffortChoice(v);
    try {
      localStorage.setItem('chat_effort', v);
    } catch { /* best-effort persistence */ }
  };

  // Ref mirror para evitar stale closure en sendMessage (M-17, igual que
  // modelChoiceRef). Si por alguna razón el state no está cargado, cae a 'medium'.
  const effortChoiceRef = useRef('medium');
  effortChoiceRef.current = effortChoice;

  // T-081: historial POR CONVERSACIÓN. El valor vive en el store persistido
  // (localStorage) — default 10 — y se mantiene durante toda la sesión y entre
  // remontajes. El ref por sesión usa el valor del store como fallback.
  const historyBySessionRef = useRef({});
  const prevSessionIdRef = useRef(null);
  const [historyLimit, setHistoryLimitState] = useState(chatHistoryLimit);
  useEffect(() => {
    const sid = sessionManager.currentSessionId;
    const map = historyBySessionRef.current;
    if (prevSessionIdRef.current == null && sid != null && map.__draft !== undefined) {
      map[sid] = map.__draft;
      delete map.__draft;
    }
    prevSessionIdRef.current = sid;
    setHistoryLimitState(map[sid ?? '__draft'] ?? chatHistoryLimit);
  }, [sessionManager.currentSessionId, chatHistoryLimit]);
  const setHistoryLimit = (value) => {
    const n = parseInt(value, 10) || 0;
    historyBySessionRef.current[sessionManager.currentSessionId ?? '__draft'] = n;
    setHistoryLimitState(n);
    setChatHistoryLimit(n);
  };

  // UI state
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // T-148: ids de hojas @-mencionadas para el próximo mensaje. Se rellena en
  // ChatInput al seleccionar del dropdown de `@`; se resetea al enviar.
  const [mentionedSheetIds, setMentionedSheetIds] = useState([]);
  // T-149b: texto del último mensaje cancelado por el usuario. Mientras no sea
  // null y no haya un envío en curso, se muestra un banner de "Reenviar".
  const [lastCancelled, setLastCancelled] = useState(null);
  // T-146: al cargar el documento el contexto por defecto es TODO EL DOCUMENTO
  // (context='full'). El selector de modo ahora vive como 3 iconos excluyentes
  // en el toolbar del header; el usuario lo cambia libremente después.
  const [context, setContext] = useState('full');
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [selection, setSelection] = useState('');
  const [includeComments, setIncludeComments] = useState(false); // T-057: adjuntar comentarios al contexto
  // T-242: modo referencias automáticas ('all' = todo el documento incluidas
  // fuentes; 'sources' = solo hojas fuente). Off por defecto; por sesión de UI
  // (sin persistencia, mismo criterio que includeComments).
  const [autoRefs, setAutoRefs] = useState(false);
  const [autoRefsScope, setAutoRefsScope] = useState('all');
  // T-242: refs espejo para evitar stale closure en sendMessage (M-17). Van
  // DESPUÉS de la declaración del estado — arriba disparaban TDZ (patrón M-71).
  const autoRefsRef = useRef(autoRefs);
  autoRefsRef.current = autoRefs;
  const autoRefsScopeRef = useRef(autoRefsScope);
  autoRefsScopeRef.current = autoRefsScope;
  const [isSelectionExpanded, setIsSelectionExpanded] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [fontSize, setFontSize] = useState('12px');
  const [showSessionList, setShowSessionList] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [newSessionPromptOpen, setNewSessionPromptOpen] = useState(false);
  // T-239: siempre inicia colapsado para dar espacio a los mensajes; el usuario lo expande al tocarlo.
  const [contextCollapsed, setContextCollapsed] = useState(true);
  // T-209: controla la apertura del SheetPicker anidado. Al hacer clic en la
  // fila "Por hoja" se setea el modo 'sheets' Y se abre este picker en la
  // misma acción (antes hacía falta un segundo clic en el chevron ›).
  const [sheetPickerOpen, setSheetPickerOpen] = useState(false);
  const [ctxDragOver, setCtxDragOver] = useState(false); // drag&drop de hoja al contexto

  // Images in the current context: opt-in to send them to the vision model. The
  // description is saved on each image (paid once, reused free). Off => the chat
  // only sees the image NAMES (backend already folds those into context).
  const [describeImages, setDescribeImages] = useState(false);
  const [excludedImageSrcs, setExcludedImageSrcs] = useState(() => new Set()); // default: none excluded = all selected
  const [imageEstimate, setImageEstimate] = useState(null);

  const scrollRef = useRef(null);

  // T-153: async job polling. `pollRef` holds the setInterval id; `activeJobRef`
  // mirrors the active job for cleanup; `lastSentMessageRef` feeds the Reenviar
  // banner (T-149) when the job ends in error/cancelled/stale; the two "last*"
  // refs track incremental progress so we only apply new sheets/rounds per tick.
  const pollRef = useRef(null);
  const activeJobRef = useRef(null);
  const lastSentMessageRef = useRef(null);
  const lastAppliedSheetsRef = useRef(0);
  const lastRoundsRef = useRef(0);
  const startJobPollingRef = useRef(null);
  // activeJobId mirrors activeJobRef for rendering (refs don't trigger re-render).
  const [activeJobId, setActiveJobId] = useState(null);
  // T-204: líneas de actividad de herramientas EN VIVO, actualizadas en cada
  // tick no-terminal. Se limpia al terminar el job (la actividad final viaja en
  // la burbuja del asistente como tool_activity, como hoy). Es estado (no un
  // array inline) para no romper el React.memo de MessageList (T-155): su
  // referencia solo cambia cuando la actualizamos, no en cada render.
  const [liveActivity, setLiveActivity] = useState([]);

  // Al elegir contexto 'hojas', la hoja ACTUAL se incluye siempre como primera
  // (el backend también la fuerza); el usuario elige el resto.
  useEffect(() => {
    if (context === 'sheets' && activeSheetId) {
      setSelectedSheets((prev) =>
        prev.includes(activeSheetId) ? prev : [activeSheetId, ...prev]);
    }
  }, [context, activeSheetId]);

  const imagesInScope = useMemo(() => {
    const acc = [];
    const scope = context === 'full'
      ? sheets
      : context === 'sheets'
        ? sheets.filter((s) => selectedSheets.includes(s.id))
        : context === 'selection'
          ? sheets.filter((s) => s.id === activeSheetId)
          : []; // 'none' = sin contexto -> ninguna imagen
    (scope || []).forEach((s) => collectImages(s.content, s.id, acc));
    return acc;
  }, [context, sheets, selectedSheets, activeSheetId]);

  const selectedImages = useMemo(
    () => imagesInScope.filter((i) => !excludedImageSrcs.has(i.src)),
    [imagesInScope, excludedImageSrcs]
  );
  // Only images WITHOUT a saved description cost credits; the rest are reused free.
  const imagesToDescribe = useMemo(
    () => selectedImages.filter((i) => !i.hasDescription),
    [selectedImages]
  );

  const toggleImageExcluded = (src) => {
    setExcludedImageSrcs((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src); else next.add(src);
      return next;
    });
  };

  // Fetch the credit estimate for the images that would actually be described.
  useEffect(() => {
    if (!describeImages) { setImageEstimate(null); return; }
    const count = imagesToDescribe.length;
    if (count === 0) { setImageEstimate({ credits: 0, count: 0, per_image: 0 }); return; }
    let cancelled = false;
    axiosInstance
      .post('/ai/describe-estimate', { count, api_key: apiKey || undefined, model })
      .then((r) => { if (!cancelled) setImageEstimate(r.data); })
      .catch(() => { if (!cancelled) setImageEstimate(null); });
    return () => { cancelled = true; };
  }, [describeImages, imagesToDescribe.length, apiKey, model]);

  // Notify parent when context changes
  useEffect(() => {
    if (onContextChange) {
      onContextChange(context);
    }
  }, [context, onContextChange]);

  // Auto-scroll al fondo cuando cambian los mensajes, al montar/desmontar el
  // spinner (T-145) y cuando aparece la barra "Cancelar proceso" (T-153): esa
  // barra encoge el contenedor 45px y dejaba el spinner fuera de vista (T-178).
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [sessionManager.messages, loading, activeJobId]);

  // T-022: report the active session id up so quick-command (Acción Rápida IA)
  // actions can persist their exchange into the SAME session the user sees.
  useEffect(() => {
    if (onSessionChange) onSessionChange(sessionManager.currentSessionId);
  }, [sessionManager.currentSessionId, onSessionChange]);

  // T-022: keep a live ref to the session manager so the event listener below can
  // be registered ONCE (no re-subscribe on every render).
  const sessionManagerRef = useRef(sessionManager);
  sessionManagerRef.current = sessionManager;

  // T-153: ref mirror so the polling interval closure always uses the latest
  // onDocumentModified (the prop is stable via useCallback in the parent, but
  // the interval captures the render-time value).
  const onDocumentModifiedRef = useRef(onDocumentModified);
  onDocumentModifiedRef.current = onDocumentModified;

  // T-022: append quick-command exchanges to the chat. Text-processing responses
  // arrive with the session id the backend persisted them to, which we adopt so the
  // history survives reload. Image descriptions arrive with sessionId=null and are
  // appended live only (their endpoint does not persist to chat sessions).
  useEffect(() => {
    const handleQuickCommandChat = (event) => {
      const sm = sessionManagerRef.current;
      const { instruction, response, sessionId } = event.detail || {};
      if (!response) return;

      if (sessionId && sessionId !== sm.currentSessionId) {
        sm.setCurrentSessionId(sessionId);
        sm.refreshSessions();
      }

      sm.setMessages((prev) => [
        ...prev,
        { role: 'user', content: instruction || '' },
        { role: 'assistant', content: response }
      ]);
    };

    window.addEventListener('quickCommandChat', handleQuickCommandChat);
    return () => window.removeEventListener('quickCommandChat', handleQuickCommandChat);
  }, []);

  // #3: after "procesar comentarios", switch to the session the server appended
  // the exchanges to (or adopt the new one) and load its messages.
  useEffect(() => {
    const handleCommentsProcessed = (event) => {
      const sm = sessionManagerRef.current;
      const sessionId = event.detail && event.detail.sessionId;
      if (!sessionId) return;
      if (sessionId === sm.currentSessionId) {
        sm.switchSession(sessionId); // reload messages of the current session
      } else {
        sm.switchSession(sessionId);
        sm.refreshSessions();
      }
    };
    window.addEventListener('commentsProcessed', handleCommentsProcessed);
    return () => window.removeEventListener('commentsProcessed', handleCommentsProcessed);
  }, []);

  // State for HTML selection (for tables and rich content)
  const [selectionHTML, setSelectionHTML] = useState('');
  const [selectionHasTables, setSelectionHasTables] = useState(false);
  const [cursorAnchor, setCursorAnchor] = useState(''); // #8: texto del bloque del caret (punto de insercion)

  // Listen for text selection events
  useEffect(() => {
    const handleTextSelection = (event) => {
      const selectedText = event.detail.text;
      const selectedHTML = event.detail.html || '';
      const hasTables = event.detail.hasTables || false;
      setCursorAnchor((event.detail.blockText || '').trim()); // #8: siempre, haya o no seleccion

      if (selectedText && selectedText.length > 0 || hasTables) {
        setSelection(selectedText);
        setSelectionHTML(selectedHTML);
        setSelectionHasTables(hasTables);
        setContext((prevContext) => {
          if (prevContext === 'none' || prevContext === 'selection') {
            return 'selection';
          }
          return prevContext;
        });
        setIsSelectionExpanded(false);
      } else {
        setSelection('');
        setSelectionHTML('');
        setSelectionHasTables(false);
        setIsSelectionExpanded(false);
      }
    };

    window.addEventListener('textSelected', handleTextSelection);
    return () => window.removeEventListener('textSelected', handleTextSelection);
  }, []);

  // Token calculation utilities
  const calculateTokens = (text) => {
    const charCount = text.length;
    return Math.ceil(charCount / 4);
  };

  const getContextTokens = () => {
    let contextText = '';

    if (context === 'full') {
      sheets.forEach(sheet => {
        contextText += extractTextFromContent(sheet.content);
      });
    } else if (context === 'sheets' && selectedSheets.length > 0) {
      selectedSheets.forEach(sheetId => {
        const sheet = sheets.find(s => s.id === sheetId);
        if (sheet) contextText += extractTextFromContent(sheet.content);
      });
    } else if (context === 'selection' && selection) {
      contextText = selection;
    }

    return calculateTokens(contextText);
  };

  const getMessageTokens = () => {
    return calculateTokens(input);
  };

  const getTotalTokens = () => {
    return getContextTokens() + getMessageTokens() + calculateTokens(documentPrompt || '');
  };

  const getContextLabel = () => {
    if (context === 'full') return 'Todo el documento';
    if (context === 'sheets' && selectedSheets.length > 0) {
      const sheetNames = selectedSheets.map(id => sheets.find(s => s.id === id)?.name).filter(Boolean).join(', ');
      return `Hojas: ${sheetNames}`;
    }
    if (context === 'selection' && selection) return 'Texto seleccionado';
    return 'Sin contexto';
  };

  // Message actions
  const copyToClipboard = useCallback(async (text, index) => {
    try {
      // Convert markdown to HTML for rich clipboard support
      const html = renderToStaticMarkup(
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      );

      if (typeof ClipboardItem !== 'undefined') {
        const blobPlain = new Blob([text], { type: 'text/plain' });
        const blobHtml = new Blob([html], { type: 'text/html' });
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': blobPlain,
            'text/html': blobHtml,
          }),
        ]);
      } else {
        // Fallback for browsers without ClipboardItem
        await navigator.clipboard.writeText(text);
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
      toast.success('Mensaje copiado');
    } catch (error) {
      console.error('Error copying message:', error);
      toast.error('Error al copiar');
    }
  }, []);

  const handleInsert = useCallback((text) => {
    if (onInsertText) {
      onInsertText(text);
      toast.success('Texto insertado');
    }
  }, [onInsertText]);

  const handleReplace = useCallback((text) => {
    if (onReplaceText) {
      onReplaceText(text);
      toast.success('Texto reemplazado');
    }
  }, [onReplaceText]);

  const handleInsertTable = useCallback((html) => {
    if (onInsertText) {
      onInsertText(html);
      toast.success('Tabla insertada en el documento');
    }
  }, [onInsertText]);

  const scrollToTop = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, []);

  // T-057: build a "Comentarios del documento:" block for the in-scope sheets,
  // referencing each comment's anchored text (the paragraph indicator) plus its
  // thread. We reference the anchor snippet — NOT the whole doc — so the document
  // context (already sent) is not duplicated.
  // ponytail: for a text selection we filter to the comment ids actually present
  // in the selected HTML; for point notes without anchor_text we label them
  // "(comentario general)" — resolving their exact paragraph would need the
  // editor doc here, out of scope for this frontend-only path.
  const buildCommentsBlock = async (sheetIds, idFilter) => {
    const seen = new Set();
    const lines = [];
    for (const sid of sheetIds) {
      if (!sid || seen.has(sid)) continue;
      seen.add(sid);
      let threads = [];
      try {
        const r = await axiosInstance.get(`/sheets/${sid}/comments`);
        threads = Array.isArray(r.data) ? r.data : [];
      } catch { continue; }
      for (const t of threads) {
        if (idFilter && !idFilter.has(t.id)) continue;
        const where = t.anchor_text
          ? `sobre «${String(t.anchor_text).trim()}»`
          : '(comentario general)';
        const status = t.status === 'resolved' ? 'resuelto' : 'abierto';
        lines.push(`- ${where} [${status}]: ${t.body}`);
        for (const rep of (t.replies || [])) lines.push(`    ↳ respuesta: ${rep.body}`);
      }
    }
    if (!lines.length) return '';
    return `\n\nComentarios del documento:\n${lines.join('\n')}`;
  };

  // T-153: poll a durable async job. The server runs the auto-continuation loop;
  // the client polls every 2.5s, applies sheet changes in vivo, refreshes
  // assistant bubbles as rounds complete, and handles terminal statuses.
  // Uses refs (sessionManagerRef, onDocumentModifiedRef) so the interval closure
  // always reads the latest values without re-subscribing.
  const startJobPolling = (jobId, sessionId) => {
    // Stop any existing poll (no two live polls).
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    activeJobRef.current = { jobId, sessionId };
    setActiveJobId(jobId);
    lastAppliedSheetsRef.current = 0;
    lastRoundsRef.current = 0;
    setLoading(true);
    // T-204: reiniciar la actividad en vivo al arrancar un nuevo job.
    setLiveActivity([]);

    const poll = async () => {
      try {
        const resp = await axiosInstance.get(`/ai/jobs/${jobId}`);
        const data = resp.data;
        const isTerminal = ['done', 'error', 'cancelled', 'stale'].includes(data.status);

        // Incremental updates (only on non-terminal ticks — the terminal handler
        // does its own final loadSessionMessages + onDocumentModified).
        if (!isTerminal) {
          if (data.modified_sheet_ids && data.modified_sheet_ids.length > lastAppliedSheetsRef.current) {
            const newIds = data.modified_sheet_ids.slice(lastAppliedSheetsRef.current);
            const fn = onDocumentModifiedRef.current;
            if (newIds.length > 0 && fn) await fn(newIds);
            lastAppliedSheetsRef.current = data.modified_sheet_ids.length;
          }
          if (data.rounds_completed > lastRoundsRef.current) {
            await sessionManagerRef.current.loadSessionMessages(sessionId);
            lastRoundsRef.current = data.rounds_completed;
          }
          // T-204: actividad de herramientas en vivo — reemplazar en cada tick.
          // El backend acumula líneas, así que data.tool_activity ya trae todas
          // las vistas hasta ahora; reemplazamos (no concatenamos) para que el
          // bloque muestre siempre la lista completa y auto-scroll al fondo.
          setLiveActivity(data.tool_activity || []);
        }

        if (isTerminal) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          activeJobRef.current = null;
          setActiveJobId(null);
          setLoading(false);
          // T-204: al terminar, limpiar la actividad en vivo. La actividad final
          // ya viaja en la burbuja del asistente como tool_activity (patch abajo).
          setLiveActivity([]);

          if (data.status === 'done') {
            await sessionManagerRef.current.loadSessionMessages(sessionId);
            if (data.modified_sheet_ids && data.modified_sheet_ids.length) {
              const fn = onDocumentModifiedRef.current;
              if (fn) await fn(data.modified_sheet_ids);
            }
            // T-183: attach to the last assistant bubble everything this turn surfaced.
            // Async migration note: fields that used to ride the sync HTTP response
            // (document_modified, tool_activity) must be re-attached here or their
            // badges in MessageList are unreachable. Empty/absent -> key not added:
            // an empty array must NOT keep alive a bubble that the T-150 empty-filter
            // would otherwise hide (and `![]` is false in JS, so check `.length`).
            const proposals = data.edit_proposals ?? (data.edit_proposal ? [data.edit_proposal] : []);
            const patch = {};
            if (proposals.length) patch.edit_proposals = proposals;
            if (data.modified_sheet_ids?.length) patch.document_modified = true;
            if (data.tool_activity?.length) patch.tool_activity = data.tool_activity;
            // T-204: metadatos de duración y créditos del job. Se calculan aquí
            // (no en el backend) con Date.parse de los ISO timestamps que llega
            // en el tick terminal. Si falta alguno → null (MessageList omite).
            // SIMPLIFICACIÓN DELIBERADA: como tool_activity, estos metadatos viven
            // solo en la sesión en vivo; al recargar el historial no aparecen.
            {
              const _sAt = data.started_at ? Date.parse(data.started_at) : null;
              const _fAt = data.finished_at ? Date.parse(data.finished_at) : null;
              patch.duration_ms = (_sAt != null && _fAt != null) ? (_fAt - _sAt) : null;
              patch.credits_charged = data.credits_charged ?? null;
            }
            // T-220: si el job modificó el documento, traer las citas que el
            // backend capturó para ESTE job (GET /documents/{documentId}/citations)
            // y filtrar por job_id. Se añaden al MISMO patch (una sola pasada de
            // setMessages, patrón T-183). En error, citations queda undefined —
            // jamás rompe el flujo del chat.
            if (data.modified_sheet_ids?.length) {
              try {
                const citResp = await axiosInstance.get(`/documents/${documentId}/citations`);
                const allCitations = Array.isArray(citResp.data) ? citResp.data : [];
                const jobCitations = allCitations.filter((c) => c.job_id === jobId);
                if (jobCitations.length > 0) patch.citations = jobCitations;
              } catch (e) {
                console.error('T-220: fetch de citas falló (no rompe el flujo):', e);
              }
            }
            if (Object.keys(patch).length) {
              sessionManagerRef.current.setMessages((prev) => {
                const msgs = [...prev];
                for (let i = msgs.length - 1; i >= 0; i--) {
                  if (msgs[i].role === 'assistant') {
                    msgs[i] = { ...msgs[i], ...patch };
                    break;
                  }
                }
                return msgs;
              });
            }
            // Refresh the credit balance (per-round charges no longer arrive
            // individually; pull the final balance from /me/credits).
            try {
              const cr = await axiosInstance.get('/me/credits');
              notifyCreditsCharged({ charged: 0, balance: cr.data?.credits_balance });
            } catch { /* balance refresh is best-effort */ }
          } else if (data.status === 'error') {
            const errMsg = data.error || 'Error en el proceso';
            if (errMsg.startsWith('402')) {
              toast.error('No tienes créditos suficientes. Compra un paquete de créditos.', {
                id: 'credits-402',
                duration: 8000,
                action: { label: 'Comprar créditos', onClick: openCreditsPanel },
              });
            } else {
              // El backend no cobra créditos cuando el job termina en error.
              toast.error(errMsg + ' No se te cobraron créditos por este proceso.');
            }
            setLastCancelled(lastSentMessageRef.current);
          } else if (data.status === 'cancelled') {
            toast.info('Proceso cancelado');
            setLastCancelled(lastSentMessageRef.current);
          } else if (data.status === 'stale') {
            toast.error('El proceso se detuvo (posible reinicio del servidor). Recarga la página para ver el trabajo ya guardado.');
            setLastCancelled(lastSentMessageRef.current);
          }
        }
      } catch (err) {
        // A single failed tick (network blip / 500) does NOT kill the job —
        // ignore and retry on the next interval.
        console.error('Poll tick failed (will retry):', err);
      }
    };

    // Fire immediately + start the interval.
    poll();
    pollRef.current = setInterval(poll, 2500);
  };
  startJobPollingRef.current = startJobPolling;

  // T-153: cancel the active job. Stops polling immediately and tells the server
  // to cancel; the Reenviar banner (T-149) is shown for the user's last message.
  const cancelActiveJob = async () => {
    const job = activeJobRef.current;
    if (!job) return;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    activeJobRef.current = null;
    setActiveJobId(null);
    setLoading(false);
    setLiveActivity([]); // T-204: limpiar actividad en vivo al cancelar.
    setLastCancelled(lastSentMessageRef.current);
    toast.info('Proceso cancelado');
    try {
      await axiosInstance.post(`/ai/jobs/${job.jobId}/cancel`);
    } catch (err) {
      console.error('Cancel request failed:', err);
    }
  };

  // Send message to AI
  const sendMessage = async (messageTextArg) => {
    const messageText = typeof messageTextArg === 'string' ? messageTextArg : input;
    if (!messageText.trim()) return;

    // T-174: texto de la plantilla de razonamiento adjunta a esta sesión.
    // Variable local desde el ref para evitar stale closure (M-17).
    const currentReasoningTemplateText = reasoningTemplateTextRef.current;

    // No client-side API-key gate: the backend falls back to a platform key
    // when the user has no BYOK key configured. Send the user's key if present.

    let sessionId = sessionManager.currentSessionId;
    if (!sessionId) {
      sessionId = await sessionManager.createNewSession();
      if (!sessionId) return;
    }

    const userMessage = { role: 'user', content: messageText };
    sessionManager.setMessages([...sessionManager.messages, userMessage]);
    setLastCancelled(null);
    setInput('');
    setMentionedSheetIds([]);
    setLoading(true);

    // T-153: stop any existing poll before starting a new send (no two live polls).
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    activeJobRef.current = null;
    setActiveJobId(null);

    try {
      // Reason: Send HTML selection when available for better context (includes tables)
      const selectionData = context === 'selection'
        ? (selectionHTML && selectionHasTables ? selectionHTML : selection)
        : null;

      // T-153: body for POST /ai/chat/async — same payload the old /ai/chat call
      // used. The server runs the auto-continuation loop as a durable job.
      const body = {
        message: messageText,
        context: context,
        sheets: context === 'sheets' ? selectedSheets : [],
        // T-026: scope tool reads to the sheet open in the editor.
        active_sheet_id: activeSheetId,
        selection: selectionData,
        selection_html: context === 'selection' && selectionHTML ? selectionHTML : null,
        has_tables: context === 'selection' ? selectionHasTables : false,
        // #8: cuando NO se manda seleccion, avisa DONDE esta el caret para insertar aqui.
        cursor_anchor: (context !== 'selection' && context !== 'none' && cursorAnchor) ? cursorAnchor : null,
        document_id: documentId,
        session_id: sessionId,
        // T-208: si el usuario eligió un modelo de la lista de chat-models,
        // mandarlo como override; si no, cae al modelo del store (BYOK) como
        // antes. "Default del sistema" → modelChoice es null → no hay override.
        model: modelChoiceRef.current || model,
        // T-226: esfuerzo de razonamiento elegido por el usuario (default high)
        effort: effortChoiceRef.current || 'medium',
        // T-242: modo referencias automáticas (protocolo inyectado por el backend).
        auto_references: autoRefsRef.current ? autoRefsScopeRef.current : null,
        system_prompt: documentPrompt || undefined,
        api_key: apiKey,
        // T-081: ventana de historial (0|10|25|50|100) elegida POR conversación.
        history_limit: historyLimit,
        // T-174: plantilla de razonamiento adjunta a la sesión. Viaja en el
        // body de cada mensaje; el backend la inyecta en el system prompt.
        reasoning_template: currentReasoningTemplateText
      };

        // T-148: @-menciones de hojas. Si el texto saliente contiene `@Nombre`
        // de hojas que el usuario seleccionó del dropdown, las añadimos al canal
        // `sheets` del backend (que ya resuelve cuáles están permitidas). Usamos
        // `messageText` (el texto que realmente se envía), NO `input` (que ya se
        // vació arriba). Filtramos menciones cuyo `@<nombre>` ya no está en el
        // texto (el usuario la borró). En modo 'full' no tocamos nada: ya incluye
        // todo el documento.
        const validMentions = (mentionedSheetIds || []).filter((id) => {
          const s = sheets.find((x) => x.id === id);
          return s && messageText.includes('@' + s.name);
        });
        if (validMentions.length > 0 && context !== 'full') {
          body.context = 'sheets';
          body.sheets = Array.from(new Set([
            ...(context === 'sheets' ? selectedSheets : []),
            ...validMentions,
          ]));
        }

      // T-057: if the user opted in, append the in-scope comments to the message
      // the model receives (the displayed user message stays clean — it was
      // pushed above as plain `input`).
      if (includeComments && context !== 'none') {
        const sheetIds = context === 'full'
          ? sheets.map((s) => s.id)
          : context === 'sheets'
            ? selectedSheets
            : [activeSheetId];
        let idFilter = null;
        if (context === 'selection') {
          const ids = selectionHTML
            ? [...selectionHTML.matchAll(/data-comment-id="([^"]+)"/g)].map((m) => m[1])
            : [];
          idFilter = new Set(ids); // empty => nothing in the selection carries a comment
        }
        const block = await buildCommentsBlock(sheetIds, idFilter);
        if (block) body.message = messageText + block;
      }

      // T-153: describe-images step (MOVED out of the old runAll loop). Runs
      // BEFORE creating the async job so the server-side context builder sees
      // the fresh descriptions. Sync call — same payload/behavior as before.
      if (describeImages && imagesToDescribe.length > 0) {
        const resp = await axiosInstance.post(
          '/ai/describe-and-save-images',
          {
            document_id: documentId,
            images: imagesToDescribe.map((i) => ({ sheet_id: i.sheet_id, src: i.src })),
            api_key: apiKey || undefined,
            model,
          }
        );
        notifyCreditsCharged(resp.data?.credits);
        if (resp.data?.modified_sheet_ids?.length && onDocumentModified) {
          await onDocumentModified(resp.data.modified_sheet_ids);
        }
      }

      // T-153: create a durable async job (replaces the old in-browser runAll
      // loop). The server runs the auto-continuation; the client polls for
      // status. No 120s timeout — the job is durable and survives reloads.
      lastSentMessageRef.current = messageText;
      let jobResp;
      try {
        jobResp = await axiosInstance.post('/ai/chat/async', body);
      } catch (err) {
        if (err.response?.status === 409) {
          toast.info('Hay un proceso en curso; espera a que termine');
          sessionManager.setMessages((prev) => prev.filter((m) => m !== userMessage));
          return;
        }
        throw err;
      }
      const { job_id, session_id: respSessionId } = jobResp.data;
      if (respSessionId && respSessionId !== sessionId) {
        sessionManager.setCurrentSessionId(respSessionId);
      }
      startJobPolling(job_id, respSessionId || sessionId);
    } catch (error) {
      // T-153: the old ERR_CANCELED path (AbortSignal from runLocked) rarely
      // applies now — polls are short requests. A non-409 failure here means the
      // POST /ai/chat/async itself failed before creating a job.
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        toast.info('Proceso cancelado');
        setLastCancelled(messageText);
      } else if (!error.__creditsHandled) {
        toast.error(error.response?.data?.detail || 'Error al comunicarse con la IA');
      }
      // Roll back the optimistic user bubble: the job was never created, so no
      // server-side work exists. (The 409 path rolls back in its own handler.)
      sessionManager.setMessages((prev) => prev.filter((m) => m !== userMessage));
    } finally {
      // Only clear loading if we are NOT polling (polling manages its own
      // loading state). If startJobPolling ran, activeJobRef is set and the poll
      // will clear loading on a terminal status.
      if (!activeJobRef.current) {
        setLoading(false);
      }
    }
  };

  // T-153: recover an in-flight job after page reload / session switch. If the
  // session has an active (queued/running) job, re-attach the polling. Avoids
  // double-poll: if already polling for THIS session, skip.
  useEffect(() => {
    const sid = sessionManager.currentSessionId;
    // If already polling for THIS session, don't interrupt (avoids stopping a
    // poll we just started when the async response adopts a session id).
    if (pollRef.current && activeJobRef.current?.sessionId === sid) return;
    // Stop any poll for a DIFFERENT session before checking recovery.
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      activeJobRef.current = null;
      setActiveJobId(null);
      setLoading(false);
      setLiveActivity([]); // T-204: limpiar actividad en vivo al cambiar de sesión.
    }
    if (!sid) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axiosInstance.get('/ai/jobs', { params: { session_id: sid, active: true } });
        if (cancelled) return;
        const job = resp.data;
        if (job && (job.status === 'queued' || job.status === 'running')) {
          startJobPollingRef.current(job.id, sid);
        }
      } catch (err) {
        console.error('Recovery check failed:', err);
      }
    })();
    return () => { cancelled = true; };
    // Intentionally only depends on currentSessionId — startJobPollingRef is a
    // ref so the effect doesn't re-run on every render.
  }, [sessionManager.currentSessionId]);

  // T-153: cleanup polling on unmount (never leave a dangling interval).
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // Botón "Nueva sesión de chat": reutiliza el mismo patrón de botón icono
  // que los Popovers vecinos. createNewSession() crea la sesión en el backend,
  // la pone al frente de la lista y limpia los mensajes visibles.
  const newSessionControl = (
    <Button
      variant="ghost"
      size="sm"
      title="Nueva sesión de chat"
      onClick={() => {
        if (sessionManager.messages.length > 0 && !sessionManager.viewingArchivedId) {
          setNewSessionPromptOpen(true);
        } else {
          sessionManager.createNewSession();
        }
      }}
      className="hover:bg-white/50 dark:hover:bg-slate-700/50"
    >
      <SquarePen className="w-4 h-4" />
      <span className="hidden sm:inline text-[10px]">Nuevo</span>
    </Button>
  );

  // T-180: menú Contexto unificado. Agrupa en un solo Popover los 3 bloques
  // que antes vivían como iconos sueltos en el toolbar (contextModeControl,
  // chatScopeControl y historyControl). El icono del trigger refleja el modo
  // activo para que el estado se vea sin abrir el menú. "Por hoja" lleva un
  // chevron que abre un submenú (Popover anidado) con el SheetPicker — el
  // dueño eligió submenú aparte, no lista inline.
  const contextControl = (
    <Popover onOpenChange={(open) => { if (!open) setSheetPickerOpen(false); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title="Contexto"
          data-testid="context-menu-button"
          className={context !== 'none'
            ? 'bg-sky-500 text-white'
            : 'hover:bg-white/50 dark:hover:bg-slate-700/50'}
        >
          {context === 'full'
            ? <FileText className="w-4 h-4" />
            : context === 'sheets'
              ? <Layers className="w-4 h-4" />
              : <Ban className="w-4 h-4" />}
          <span className="hidden sm:inline text-[10px]">Contexto</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        {/* ── Bloque 1: Contenido que se envía ── */}
        <div className="text-xs uppercase text-slate-500 dark:text-slate-400 px-2 pt-2 pb-1">
          Contenido que se envía
        </div>
        <button
          type="button"
          data-testid="context-mode-full"
          title="Envía todo el contenido del documento como contexto a la IA. Útil para preguntas generales sobre el documento completo."
          onClick={() => setContext('full')}
          className={`w-full text-left px-2 py-1.5 text-sm rounded flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 ${context === 'full' ? 'font-semibold text-sky-600 dark:text-sky-300' : 'text-slate-700 dark:text-slate-200'}`}
        >
          <FileText className="w-4 h-4 shrink-0" />
          <span className="flex-1">Todo el documento</span>
        </button>
        {/* Fila "Por hoja": el chevron de la derecha abre un submenú anidado
            con el SheetPicker. El div (role=button) activa el modo 'sheets';
            el chevron tiene stopPropagation para no disparar el onClick del
            div al abrir únicamente el submenú.
            T-209: al hacer clic en el div (no solo en el chevron) se setea el
            modo 'sheets' Y se abre el SheetPicker en la misma acción — antes
            hacía falta un segundo clic en el chevron. */}
        <div
          role="button"
          tabIndex={0}
          data-testid="context-mode-sheets"
          title="Elige qué hojas enviar como contexto. La hoja activa siempre se incluye. Clic para abrir el selector de hojas."
          onClick={() => { setContext('sheets'); setSheetPickerOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setContext('sheets');
              setSheetPickerOpen(true);
            }
          }}
          className={`w-full text-left px-2 py-1.5 text-sm rounded flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 ${context === 'sheets' ? 'font-semibold text-sky-600 dark:text-sky-300' : 'text-slate-700 dark:text-slate-200'}`}
        >
          <Layers className="w-4 h-4 shrink-0" />
          <span className="flex-1">Por hoja</span>
          <Popover open={sheetPickerOpen} onOpenChange={setSheetPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="context-mode-sheets-submenu"
                onClick={(e) => e.stopPropagation()}
                className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 shrink-0"
                title="Abrir selector de hojas para elegir cuáles enviar como contexto"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent side="left" align="start" className="w-64">
              <SheetPicker
                sheets={sheets}
                activeSheetId={activeSheetId}
                selectedSheets={selectedSheets}
                setSelectedSheets={setSelectedSheets}
              />
            </PopoverContent>
          </Popover>
        </div>
        <button
          type="button"
          data-testid="context-mode-none"
          title="No envía contenido del documento. La IA responde solo con tu mensaje y su conocimiento general."
          onClick={() => setContext('none')}
          className={`w-full text-left px-2 py-1.5 text-sm rounded flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 ${context === 'none' ? 'font-semibold text-sky-600 dark:text-sky-300' : 'text-slate-700 dark:text-slate-200'}`}
        >
          <Ban className="w-4 h-4 shrink-0" />
          <span className="flex-1">Sin contexto</span>
        </button>

        <div className="h-px bg-slate-200 dark:bg-slate-700 my-1" />

        {/* ── Bloque 2: Conversación ── */}
        <div className="text-xs uppercase text-slate-500 dark:text-slate-400 px-2 pt-2 pb-1">
          Conversación
        </div>
        <button
          type="button"
          data-testid="chat-scope-toggle"
          title={chatGlobal
            ? 'Chat global: una conversación para todo el documento. Clic para cambiar a chat por hoja.'
            : 'Chat por hoja: una conversación distinta por cada hoja. Clic para cambiar a chat global.'}
          onClick={() => setChatGlobal(!chatGlobal)}
          className={`w-full text-left px-2 py-1.5 text-sm rounded flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 ${chatGlobal ? 'font-semibold text-sky-600 dark:text-sky-300' : 'text-slate-700 dark:text-slate-200'}`}
        >
          {chatGlobal
            ? <Globe className="w-4 h-4 shrink-0" />
            : <SquareStack className="w-4 h-4 shrink-0" />}
          <span className="flex-1">{chatGlobal ? 'Chat global' : 'Chat por hoja'}</span>
        </button>

        <div className="h-px bg-slate-200 dark:bg-slate-700 my-1" />

        {/* ── Bloque 3: Memoria ── */}
        <div className="text-xs uppercase text-slate-500 dark:text-slate-400 px-2 pt-2 pb-1">
          Memoria
        </div>
        <div className="px-2">
          <HistoryLimitControl
            historyLimit={historyLimit}
            setHistoryLimit={setHistoryLimit}
          />
        </div>
      </PopoverContent>
    </Popover>
  );

  // T-174: control de plantilla de razonamiento. Un Popover (patrón de
  // historyControl, M-12) que lista los comandos de texto (T-237) +
  // una opción "Ninguna". El estado ACTIVO es visible: bg-sky-500 text-white
  // cuando hay plantilla adjunta (igual que contextModeControl / chatScopeControl).
  const reasoningTemplateControl = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title="Plantilla de razonamiento"
          data-testid="reasoning-template-button"
          className={effectiveReasoningTemplateId
            ? 'bg-sky-500 text-white'
            : 'hover:bg-white/50 dark:hover:bg-slate-700/50'}
        >
          <ClipboardList className="w-4 h-4" />
          <span className="hidden sm:inline text-[10px]">Plantilla</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56">
        <p className="text-xs text-slate-500 dark:text-slate-400 px-2 pt-2 pb-1">
          La plantilla da estilo y personalidad a TODA esta sesión: su texto se antepone a cada mensaje. No ejecuta pasos ni usa asesores; para eso está «Flujos IA» en la barra del editor.
        </p>
        <div className="space-y-1">
          <button
            type="button"
            data-testid="reasoning-template-none"
            title="Sin plantilla de razonamiento. La IA usa solo el prompt del documento."
            onClick={() => handleReasoningTemplateChange(null)}
            className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-700 ${!effectiveReasoningTemplateId ? 'font-semibold text-sky-600 dark:text-sky-300' : 'text-slate-700 dark:text-slate-200'}`}
          >
            Ninguna
          </button>
          {chatTemplateCommands.length === 0 && (
            <p className="px-2 py-1.5 text-xs text-slate-500 dark:text-slate-400">
              No hay comandos de texto para usar como plantilla. Crea uno con &laquo;Nueva plantilla&raquo; aquí abajo.
            </p>
          )}
          {chatTemplateCommands.map((cmd) => {
            const firstStep = (cmd.steps && cmd.steps[0] && cmd.steps[0].instruction) || '';
            const desc = firstStep.length > 80 ? firstStep.slice(0, 77) + '…' : firstStep;
            return (
              <div key={cmd.id} className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid={`reasoning-template-option-${cmd.id}`}
                  title={desc || cmd.title}
                  onClick={() => handleReasoningTemplateChange(cmd.id)}
                  className={`flex-1 text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-700 ${effectiveReasoningTemplateId === cmd.id ? 'font-semibold text-sky-600 dark:text-sky-300' : 'text-slate-700 dark:text-slate-200'}`}
                >
                  {cmd.title}
                </button>
                {/* T-212 (ítem 19): lápiz para editar la plantilla. Los comandos
                    system son de solo lectura — se oculta el lápiz (regla del gestor). */}
                {!cmd.system && onOpenCommandManager && (
                  <button
                    type="button"
                    data-testid={`reasoning-template-edit-${cmd.id}`}
                    title="Editar comando"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenCommandManager({ type: 'edit', commandId: cmd.id });
                    }}
                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 shrink-0 text-slate-500 dark:text-slate-400"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
          {/* T-212 (ítem 19): acceso directo al gestor para crear/editar plantillas. */}
          {onOpenCommandManager && (
            <>
              <div className="h-px bg-slate-200 dark:bg-slate-700 my-1" />
              <button
                type="button"
                data-testid="reasoning-template-new"
                title="Crear una nueva plantilla de chat"
                onClick={() => onOpenCommandManager({ type: 'create' })}
                className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-sky-600 dark:text-sky-300 flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                Nueva plantilla…
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );

  // T-208: selector de modelo para el chat. Botón con Popover (patrón de
  // reasoningTemplateControl, M-12) que lista los modelos de GET /ai/chat-models:
  // nombre (model_id), descripción y costo aproximado si trae pricing.
  // Opción "Default del sistema" arriba (= no mandar model override).
  // Si la lista está vacía → no se renderiza el botón. Estado activo visible
  // (bg-sky-500 text-white cuando hay elección) + title con el modelo elegido.
  const modelSelectorControl = chatModels.length > 0 ? (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title={modelChoice ? `Modelo: ${modelChoice}` : 'Modelo (default del sistema)'}
          data-testid="chat-model-button"
          className={modelChoice
            ? 'bg-sky-500 text-white'
            : 'hover:bg-white/50 dark:hover:bg-slate-700/50'}
        >
          <Cpu className="w-4 h-4" />
          <span className="hidden sm:inline text-[10px]">Modelo</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <p className="text-xs text-slate-500 dark:text-slate-400 px-2 pt-2 pb-1">
          Elige el modelo para el chat. Aplica a todas tus conversaciones.
        </p>
        <div className="space-y-1">
          <button
            type="button"
            data-testid="chat-model-default"
            title="Usa el modelo por defecto del sistema"
            onClick={() => handleModelChoice(null)}
            className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-700 ${!modelChoice ? 'font-semibold text-sky-600 dark:text-sky-300' : 'text-slate-700 dark:text-slate-200'}`}
          >
            Default del sistema
          </button>
          {chatModels.map((m) => (
            <button
              key={m.model_id}
              type="button"
              data-testid={`chat-model-option-${m.model_id}`}
              title={m.description || m.model_id}
              onClick={() => handleModelChoice(m.model_id)}
              className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-700 ${modelChoice === m.model_id ? 'font-semibold text-sky-600 dark:text-sky-300' : 'text-slate-700 dark:text-slate-200'}`}
            >
              <div className="font-medium">{m.model_id}</div>
              {m.description && (
                <div className="text-xs text-slate-500 dark:text-slate-400">{m.description}</div>
              )}
              {(m.input_cost_per_mtok != null || m.output_cost_per_mtok != null) && (
                <div className="text-xs text-slate-400 dark:text-slate-500">
                  ${m.input_cost_per_mtok ?? '—'}/M in · ${m.output_cost_per_mtok ?? '—'}/M out
                </div>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  ) : null;

  // T-226: selector de esfuerzo de razonamiento. Copia el patrón EXACTO de
  // modelSelectorControl (Popover + Button ghost sm + PopoverContent w-72 +
  // botones de lista). Siempre visible: siempre hay un valor (default 'medium'),
  // no hay estado "sin elegir" → el trigger es neutro (sin bg-sky-500).
  const effortLabel = (EFFORT_OPTIONS.find((o) => o.value === effortChoice) || EFFORT_OPTIONS[2]).label;
  const effortSelectorControl = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title={`Esfuerzo de razonamiento: ${effortLabel}`}
          data-testid="chat-effort-button"
          className="hover:bg-white/50 dark:hover:bg-slate-700/50"
        >
          <Gauge className="w-4 h-4" />
          <span className="hidden sm:inline text-[10px]">Esfuerzo</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <p className="text-xs text-slate-500 dark:text-slate-400 px-2 pt-2 pb-1">
          Cuánto razona la IA antes de responder. Más esfuerzo = mejores respuestas en tareas difíciles, pero más lento y consume más créditos.
        </p>
        <div className="space-y-1">
          {EFFORT_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              data-testid={`chat-effort-option-${o.value}`}
              title={o.desc}
              onClick={() => handleEffortChoice(o.value)}
              className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-700 ${effortChoice === o.value ? 'font-semibold text-sky-600 dark:text-sky-300' : 'text-slate-700 dark:text-slate-200'}`}
            >
              <div className="font-medium">{o.label}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{o.desc}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );

  // T-239: control compacto de adjuntos del contexto (comentarios + imágenes).
  // Libera espacio vertical: los dos adjuntos viven juntos en un popover a la
  // derecha del control de Esfuerzo, en vez de ocupar líneas sobre el input.
  const attachmentsControl = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid="chat-attachments-button"
          title="Adjuntos del contexto: comentarios e imágenes"
          className={(includeComments || describeImages)
            ? 'bg-sky-500 text-white'
            : 'hover:bg-white/50 dark:hover:bg-slate-700/50'}
        >
          <Paperclip className="w-4 h-4" />
          <span className="hidden sm:inline text-[10px]">Adjuntos</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-3">
          {/* T-239: checkbox de comentarios movido aquí desde ContextControls */}
          <label className="flex items-center gap-2 text-xs text-sky-800 dark:text-sky-200 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="include-comments-checkbox"
              checked={!!includeComments}
              onChange={(e) => setIncludeComments(e.target.checked)}
              className="accent-sky-600"
              title="Adjunta los comentarios del documento (con sus hilos y respuestas) al contexto que recibe la IA, para que pueda responder sobre ellos."
            />
            Incluir comentarios del documento
          </label>
          {/* T-239: barra de imágenes movida aquí desde sobre el input */}
          <DescribeImagesBar
            imagesInScope={imagesInScope}
            describeImages={describeImages} setDescribeImages={setDescribeImages}
            excludedImageSrcs={excludedImageSrcs} toggleImageExcluded={toggleImageExcluded}
            imagesToDescribe={imagesToDescribe} imageEstimate={imageEstimate}
          />
        </div>
      </PopoverContent>
    </Popover>
  );

  // T-242: control de referencias automáticas. Popover con checkbox de
  // activación y radios de alcance (todo el documento o solo hojas fuente).
  // Mismo patrón que attachmentsControl (Popover > PopoverTrigger > Button).
  const referencesControl = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid="chat-references-button"
          title="Referencias automáticas: la IA busca párrafos que respalden lo trabajado y crea comentarios con la cita literal y el motivo"
          className={autoRefs
            ? 'bg-sky-500 text-white'
            : 'hover:bg-white/50 dark:hover:bg-slate-700/50'}
        >
          <BookMarked className="w-4 h-4" />
          <span className="hidden sm:inline text-[10px]">Referencias</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <p className="text-xs text-slate-500 dark:text-slate-400 px-2 pt-2 pb-1">Al responder, la IA busca en el alcance elegido los párrafos que coincidan con o respalden lo trabajado y crea un comentario por cada uno, con la cita literal, el motivo y la hoja de origen. Usa créditos extra (búsqueda + comentarios).</p>
        <label className="flex items-center gap-2 text-xs text-sky-800 dark:text-sky-200 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="auto-refs-checkbox"
            checked={autoRefs}
            onChange={(e) => setAutoRefs(e.target.checked)}
            className="accent-sky-600"
          />
          Buscar y crear referencias automáticamente
        </label>
        <div className={`mt-2 space-y-1 ${autoRefs ? '' : 'opacity-50'}`}>
          <label className="flex items-center gap-2 text-xs text-sky-800 dark:text-sky-200 cursor-pointer select-none">
            <input
              type="radio"
              name="auto-refs-scope"
              value="all"
              data-testid="auto-refs-scope-all"
              checked={autoRefsScope === 'all'}
              onChange={(e) => setAutoRefsScope('all')}
              disabled={!autoRefs}
              className="accent-sky-600"
            />
            Todo el documento (incluye fuentes)
          </label>
          <label className="flex items-center gap-2 text-xs text-sky-800 dark:text-sky-200 cursor-pointer select-none">
            <input
              type="radio"
              name="auto-refs-scope"
              value="sources"
              data-testid="auto-refs-scope-sources"
              checked={autoRefsScope === 'sources'}
              onChange={(e) => setAutoRefsScope('sources')}
              disabled={!autoRefs}
              className="accent-sky-600"
            />
            Solo hojas fuente
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );

  // T-151: ocultar del chat los prompts internos de auto-continuación. El
  // estado real (sessionManager.messages) NO se toca — el rollback del catch,
  // el auto-scroll y messages.length siguen sobre la lista real; solo se filtra
  // lo que se le pasa a <MessageList> para renderizar.
  const visibleMessages = useMemo(
    () => (sessionManager.messages || []).filter(
      (m) => !(m.role === 'user' && m.content === AUTO_CONTINUE_PROMPT)
    ),
    [sessionManager.messages]
  );

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="chat-sidebar">
      {/* Header */}
      <ChatHeader
        sessions={sessionManager.sessions}
        showSessionList={showSessionList}
        setShowSessionList={setShowSessionList}
        fontSize={fontSize}
        setFontSize={setFontSize}
        onToggleSize={onToggleSize}
        isExpanded={isExpanded}
        onClose={onClose}
        onClear={() => setConfirmClearOpen(true)}
        newSessionControl={newSessionControl}
        contextControl={contextControl}
        reasoningTemplateControl={reasoningTemplateControl}
      />

      {/* History panel (read-only archived conversations) */}
      {showSessionList && (
        <SessionList
          history={sessionManager.history}
          loadingSessions={sessionManager.loadingSessions}
          loadHistory={sessionManager.loadHistory}
          viewingArchivedId={sessionManager.viewingArchivedId}
          viewArchived={sessionManager.viewArchived}
          exitArchived={sessionManager.exitArchived}
          confirmDeleteSession={sessionManager.confirmDeleteSession}
          onResume={sessionManager.resumeSession}
        />
      )}

      {/* Selector de contexto colapsable: al colapsar queda un solo renglón con
          la etiqueta del contexto activo + tokens insertados; al expandir
          muestra el <ContextControls> completo (como antes). */}
      {!sessionManager.viewingArchivedId && (
        <div
          className={`border-b border-white/20 ${ctxDragOver ? 'ring-2 ring-sky-400 ring-inset bg-sky-500/10' : ''}`}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('application/x-sheet-id')) {
              e.preventDefault();
              setCtxDragOver(true);
            }
          }}
          onDragLeave={() => setCtxDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setCtxDragOver(false);
            const id = e.dataTransfer.getData('application/x-sheet-id');
            if (!id) return;
            if (context === 'full') return; // ya incluye todo
            setContext('sheets');
            setSelectedSheets((prev) => (prev.includes(id) ? prev : [...prev, id]));
          }}
        >
          <button
            type="button"
            onClick={() => setContextCollapsed((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-white/20"
            title={contextCollapsed ? 'Expandir contexto' : 'Colapsar contexto'}
          >
            {(() => {
              const tipo = context === 'full' ? 'Todo el documento'
                : context === 'sheets' ? 'Hojas seleccionadas'
                : context === 'selection' ? 'Selección' : 'Sin contexto';
              const nHojas = context === 'full' ? sheets.length
                : context === 'sheets' ? selectedSheets.length : 0;
              const fila1 = [tipo];
              if (nHojas > 0) fila1.push(`${nHojas} hoja${nHojas === 1 ? '' : 's'}`);
              const fila2 = [];
              if (context !== 'none') {
                fila1.push(`≈ ${tokensToWords(getContextTokens()).toLocaleString()} palabras`);
                fila2.push(`Comentarios: ${includeComments ? 'sí' : 'no'}`);
                // T-194: indicador de plantilla de razonamiento activa junto al
                // resumen de Comentarios (mismo patrón: push + join ' · ').
                // effectiveReasoningTemplateId ya devuelve null si el id guardado
                // ya no existe en la lista (comando borrado) -> "Plantilla: ninguna".
                const tplCmd = effectiveReasoningTemplateId
                  ? chatTemplateCommands.find((c) => c.id === effectiveReasoningTemplateId)
                  : null;
                fila2.push(tplCmd
                  ? `Plantilla activa: ${tplCmd.title}`
                  : 'Plantilla: ninguna');
              }
              // T-240: en ancho normal el resumen se parte en 2 filas (desde
              // "Comentarios" abajo); expandido cabe en una sola línea.
              if (isExpanded || fila2.length === 0) {
                return <span className="truncate">{[...fila1, ...fila2].join(' · ')}</span>;
              }
              return (
                <span className="flex flex-col items-start text-left min-w-0">
                  <span className="truncate w-full">{fila1.join(' · ')}</span>
                  <span className="truncate w-full">{fila2.join(' · ')}</span>
                </span>
              );
            })()}
            {contextCollapsed ? <ChevronDown className="w-5 h-5 shrink-0 text-blue-500 dark:text-blue-400" /> : <ChevronUp className="w-5 h-5 shrink-0 text-blue-500 dark:text-blue-400" />}
          </button>
          {!contextCollapsed && (
            <div className="px-3 pb-3 overflow-y-auto" style={{ maxHeight: '55vh' }}>
              <ContextControls
                context={context} setContext={setContext}
                sheets={sheets} activeSheetId={activeSheetId}
                selectedSheets={selectedSheets} setSelectedSheets={setSelectedSheets}
                selection={selection} setSelection={setSelection}
                isSelectionExpanded={isSelectionExpanded} setIsSelectionExpanded={setIsSelectionExpanded}
                getContextLabel={getContextLabel} getContextTokens={getContextTokens}
                getMessageTokens={getMessageTokens} getTotalTokens={getTotalTokens}
                input={input}
              />
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <MessageList
        messages={visibleMessages}
        loading={loading}
        liveActivity={liveActivity}
        fontSize={fontSize}
        copiedIndex={copiedIndex}
        copyToClipboard={copyToClipboard}
        handleInsert={handleInsert}
        handleReplace={handleReplace}
        scrollRef={scrollRef}
        scrollToTop={scrollToTop}
        scrollToBottom={scrollToBottom}
        onInsertTable={handleInsertTable}
        onApplyProposal={onApplyProposal}
        documentId={documentId}
      />

      {/* Input — replaced by a read-only banner while viewing an archived chat. */}
      {sessionManager.viewingArchivedId ? (
        <div className="p-3 border-t border-white/20 flex items-center justify-between gap-2 text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/40">
          <span>Viendo el historial (solo lectura).</span>
          <button
            onClick={sessionManager.exitArchived}
            className="px-2 py-1 rounded bg-sky-500 hover:bg-sky-600 text-white"
          >
            Volver al chat
          </button>
        </div>
      ) : (
        <>
          {lastCancelled && !loading && (
            <div
              data-testid="resend-banner"
              className="mx-4 mb-2 flex items-center justify-between gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
            >
              <span>Mensaje cancelado.</span>
              <Button
                size="sm"
                variant="ghost"
                data-testid="resend-button"
                onClick={() => { const t = lastCancelled; setLastCancelled(null); sendMessage(t); }}
              >
                <RotateCcw className="w-4 h-4" />
                Reenviar
              </Button>
            </div>
          )}
          {loading && activeJobId && (
            <div className="px-4 py-1.5 flex justify-center border-t border-white/20">
              <Button
                variant="outline"
                size="sm"
                onClick={cancelActiveJob}
                className="text-xs"
                data-testid="cancel-job-button"
              >
                <Ban className="w-3.5 h-3.5" /> Cancelar proceso
              </Button>
            </div>
          )}
          <ChatInput
            input={input}
            setInput={setInput}
            loading={loading}
            sendMessage={sendMessage}
            sheets={sheets}
            mentionedSheetIds={mentionedSheetIds}
            setMentionedSheetIds={setMentionedSheetIds}
            modelSelectorControl={modelSelectorControl}
            effortSelectorControl={effortSelectorControl}
            attachmentsControl={attachmentsControl}
            referencesControl={referencesControl}
          />
        </>
      )}

      {/* Soft-delete confirmation dialog */}
      <DeleteSessionDialog
        sessionToDelete={sessionManager.sessionToDelete}
        setSessionToDelete={sessionManager.setSessionToDelete}
        deleteSession={sessionManager.deleteSession}
      />

      {/* "Nueva conversación" prompt: archive or delete the current chat before
          starting a fresh one (only when the current chat has messages). */}
      <NewSessionPromptDialog
        open={newSessionPromptOpen}
        onOpenChange={setNewSessionPromptOpen}
        onSave={async () => {
          await sessionManager.clearChat();
          setNewSessionPromptOpen(false);
        }}
        onDelete={async () => {
          await sessionManager.discardSession(sessionManager.currentSessionId);
          setNewSessionPromptOpen(false);
        }}
      />

      {/* T9: confirmación antes de archivar el chat al limpiar. */}
      <ConfirmDialog
        open={confirmClearOpen}
        onOpenChange={setConfirmClearOpen}
        title="Limpiar chat"
        description="La conversación actual se guardará en el historial y el chat quedará vacío. ¿Continuar?"
        confirmText="Limpiar"
        onConfirm={() => {
          sessionManager.clearChat();
          setConfirmClearOpen(false);
        }}
      />
    </div>
  );
};

export default ChatSidebar;
